import { useState, useEffect } from 'react'
import { X, Download } from 'lucide-react'

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

const DISMISS_KEY = 'construzilla-pwa-dismissed'

export default function PwaInstallBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [show, setShow] = useState(false)

  useEffect(() => {
    // Don't show if already dismissed or already installed as PWA
    if (localStorage.getItem(DISMISS_KEY)) return
    if (window.matchMedia('(display-mode: standalone)').matches) return

    const handler = (e: Event) => {
      e.preventDefault()
      setDeferredPrompt(e as BeforeInstallPromptEvent)
      setShow(true)
    }

    window.addEventListener('beforeinstallprompt', handler)
    return () => window.removeEventListener('beforeinstallprompt', handler)
  }, [])

  // Listen for SW update toast
  useEffect(() => {
    const handler = () => {
      // Use a simple confirm for update notification
      if (window.confirm('Nova versão disponível — clique OK para atualizar')) {
        window.location.reload()
      }
    }
    window.addEventListener('sw-update-available', handler)
    return () => window.removeEventListener('sw-update-available', handler)
  }, [])

  const handleInstall = async () => {
    if (!deferredPrompt) return
    await deferredPrompt.prompt()
    const { outcome } = await deferredPrompt.userChoice
    if (outcome === 'accepted') {
      setShow(false)
    }
    setDeferredPrompt(null)
  }

  const handleDismiss = () => {
    setShow(false)
    localStorage.setItem(DISMISS_KEY, '1')
  }

  if (!show) return null

  return (
    <div
      className="fixed bottom-4 left-4 right-4 z-[9999] flex items-center gap-3 rounded-xl px-4 py-3 shadow-xl border md:left-auto md:right-6 md:max-w-sm animate-fade-up"
      style={{
        background: 'hsl(var(--bg2))',
        borderColor: 'hsl(var(--line2))',
      }}
    >
      <img src="/construzilla-logo.png?v=6" alt="" style={{ height: 32, flexShrink: 0, background: 'transparent', border: 'none', borderRadius: 0, boxShadow: 'none', padding: 0 }} />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-bold text-foreground truncate">Instale o Construzilla</div>
        <div className="text-xs text-muted-foreground">Acesse direto do celular</div>
      </div>
      <button
        onClick={handleInstall}
        className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-colors"
        style={{ background: 'hsl(var(--amber))', color: 'hsl(var(--bg0))' }}
      >
        <Download className="w-3.5 h-3.5" />
        Instalar
      </button>
      <button
        onClick={handleDismiss}
        className="flex-shrink-0 p-1 rounded-lg text-muted-foreground hover:text-foreground transition-colors"
        aria-label="Fechar"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  )
}
